import { useState } from 'react';
import { OTPInput } from '@/components/auth/OTPInput';
import { supabase } from '@/lib/supabase';

interface PhoneVerificationProps {
  email: string;
  name: string;
  onVerificationComplete: (verified: boolean, token?: string) => void;
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
  className?: string;
}

export function PhoneVerification({ 
  email, 
  name,
  onVerificationComplete, 
  onError, 
  onSuccess,
  className = '' 
}: PhoneVerificationProps) {
  const [showOTPInput, setShowOTPInput] = useState(false);
  const [isOTPLoading, setIsOTPLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [remainingAttempts, setRemainingAttempts] = useState<number | undefined>();
  const [, setNextAllowedAt] = useState<string | undefined>();
  const [isEmailVerified, setIsEmailVerified] = useState(false);

  // OTP 발송
  const handleSendOTP = async () => {
    if (!email) {
      onError('이메일을 입력해주세요');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      onError('올바른 이메일 형식을 입력해주세요');
      return;
    }

    setIsResending(true);
    setOtpError('');

    try {
      const { data, error } = await supabase.functions.invoke('send-otp', {
        body: { 
          email: email,
          purpose: 'signup',
          name: name
        }
      });

      if (error) {
        throw error;
      }

      if (data?.success) {
        setShowOTPInput(true);
        setRemainingAttempts(data.remainingAttempts);
        onSuccess(data.message);
      } else {
        setOtpError(data?.error || '인증번호 발송에 실패했습니다.');
        setRemainingAttempts(data?.remainingAttempts);
        setNextAllowedAt(data?.nextAllowedAt);
      }
    } catch (error: any) {
      console.error('OTP 발송 오류:', error);
      setOtpError(error.message || '인증번호 발송 중 오류가 발생했습니다.');
    } finally {
      setIsResending(false);
    }
  };

  // OTP 검증
  const handleVerifyOTP = async (otp: string) => {
    setIsOTPLoading(true);
    setOtpError('');

    try {
      const { data, error } = await supabase.functions.invoke('verify-otp', {
        body: { 
          email: email,
          otp,
          purpose: 'signup'
        }
      });

      if (error) {
        throw error;
      }

      if (data?.success) {
        setIsEmailVerified(true);
        setShowOTPInput(false);
        onVerificationComplete(true, data.verificationToken);
        onSuccess('이메일 인증이 완료되었습니다!');
      } else {
        setOtpError(data?.error || '인증번호가 올바르지 않습니다.');
        setRemainingAttempts(data?.remainingAttempts);
      }
    } catch (error: any) {
      console.error('OTP 검증 오류:', error);
      setOtpError(error.message || '인증번호 확인 중 오류가 발생했습니다.');
    } finally {
      setIsOTPLoading(false);
    }
  };

  if (isEmailVerified) {
    return (
      <div className={`bg-green-50 border border-green-200 rounded-lg p-4 ${className}`}>
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
          </svg>
          <span className="text-[16px] font-medium text-green-700">이메일 인증 완료</span>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="mb-4">
        <label className="block text-[18px] font-medium text-[#222222] mb-3">
          이메일 인증
        </label>
        
        {!showOTPInput ? (
          <div className="space-y-3">
            <div className="flex gap-3">
              <input
                type="email"
                value={email}
                readOnly
                className="flex-1 h-[48px] px-5 text-[18px] border-[1.5px] border-[#979797] rounded-[12px] bg-gray-50 text-gray-700"
              />
              <button
                onClick={handleSendOTP}
                disabled={isResending || !email}
                className="px-4 py-2 bg-[#51C854] text-white font-medium text-[14px] rounded-lg hover:bg-[#46B649] transition-colors disabled:opacity-50"
              >
                {isResending ? '인증중...' : '인증하기'}
              </button>
            </div>
            
            {otpError && (
              <div className="text-red-500 text-[14px]">{otpError}</div>
            )}
            
            {remainingAttempts !== undefined && (
              <div className="text-[14px] text-gray-600">
                남은 시도 횟수: {remainingAttempts}회
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-[16px] text-gray-600">
              {email}로 발송된 인증번호를 입력해주세요
            </p>
            
            <OTPInput 
              onComplete={handleVerifyOTP}
              isLoading={isOTPLoading}
              error={otpError}
              onResend={handleSendOTP}
              isResending={isResending}
              remainingAttempts={remainingAttempts}
            />
          </div>
        )}
      </div>
    </div>
  );
}